import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, MessageSquare, Search } from 'lucide-react';
import VenueBottomNav from '../../components/VenueBottomNav';
import VenueContext from '../../contexts/VenueContext';
import { BellIcon } from '../../components/BellIcon';
import NotificationBellButton from '../../components/NotificationBellButton';
import { MessengerListShell } from '../../components/messaging/MessengerListShell';
import { MessengerListRow } from '../../components/messaging/MessengerListRow';
import { buildVenueConversations } from '../../utils/venueDemoMessages';
import { buildMessageHeaders } from '../../utils/messageHeaders';
import VenueEmptyState from '../../components/venue/VenueEmptyState';
import { VenueGuestMessagesOffPill } from '../../components/venue/VenueGuestMessagesOffPill';
import { fetchAccountSettings } from '../../utils/accountSettingsApi';
import { formatChatDisplayName } from '../../utils/nameFormatters';
import { SupportRowBadge } from '../../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VenueMessagesPage() {
  const navigate = useNavigate();
  const venueCtx = useContext(VenueContext);
  const activeVenue = venueCtx?.activeVenue;
  const activeVenueAppId = venueCtx?.activeVenueAppId;
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [liveConversations, setLiveConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiveMessages, setReceiveMessages] = useState(true);

  useEffect(() => {
    fetchAccountSettings()
      .then((settings) => setReceiveMessages(settings.privacy?.receive_venue_messages !== false))
      .catch(() => {});
  }, []);

  const token = useMemo(() => localStorage.getItem('token'), []);

  const fetchConversations = useCallback(async () => {
    if (!token || !activeVenueAppId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: buildMessageHeaders(token, activeVenueAppId, 'venue'),
      });
      setLiveConversations(response.data || []);
    } catch (error) {
      console.error('Failed to load venue conversations', error);
    } finally {
      setLoading(false);
    }
  }, [activeVenueAppId, token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const demoConversations = useMemo(() => buildVenueConversations(activeVenue), [activeVenue?.id, activeVenue?.restaurant_name]);
  const conversations = activeVenueAppId ? liveConversations : demoConversations;

  const filteredConversations = useMemo(() => conversations.filter((conversation) => {
    const name = conversation.other_user?.name || conversation.name || '';
    const type = conversation.type === 'venue' ? 'customer' : conversation.type;
    const matchesSearch = !search || name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || type === filter || (filter === 'support' && conversation.type === 'support');
    return matchesSearch && matchesFilter;
  }), [conversations, filter, search]);

  // When the venue has receiving OFF, only GUEST conversations are blocked.
  // Internal team chats (venue ↔ admin ↔ staff) can never be turned off, so we
  // keep every non-guest conversation visible and just hide guest ones + show
  // the note. (Demo conversations have no role and stay visible.)
  const visibleConversations = receiveMessages
    ? filteredConversations
    : filteredConversations.filter((conversation) => conversation.other_user?.role !== 'guest');

  const filterNode = (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
      <button onClick={() => setFilterOpen((prev) => !prev)} data-testid="venue-messages-filter-button" className="premium-card-compact premium-card-nested" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', cursor: 'pointer' }}>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text)' }}>{filter === 'all' ? 'All messages' : filter === 'customer' ? 'Customers' : filter === 'support' ? 'Support' : 'Members'}</span>
        <ChevronDown size={16} color="var(--text)" style={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
      </button>
      {filterOpen ? (
        <div className="premium-card-compact premium-card-nested" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, minWidth: 150, marginTop: 6, overflow: 'hidden' }}>
          {[
            ['all', 'All messages'],
            ['customer', 'Customers'],
            ['member', 'Members'],
            ['support', 'Support'],
          ].map(([value, label]) => (
            <button key={value} data-testid={`venue-messages-filter-${value}`} onClick={() => { setFilter(value); setFilterOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 15, color: filter === value ? 'var(--accent-purple)' : 'var(--text-2)' }}>{label}</button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <MessengerListShell
      pageTestId="venue-messages-page"
      bellNode={<NotificationBellButton buttonTestId="venue-messages-bell-button" popupTestId="venue-messages-notification-popup" />}
      bottomNav={<VenueBottomNav />}
      title="Messages"
      searchValue={search}
      onSearchChange={(event) => setSearch(event.target.value)}
      searchPlaceholder="Guest or staff name"
      searchTestId="venue-messages-search-input"
      searchIcon={<Search size={18} color="var(--text-3)" />}
      cardHeader={filterNode}
    >
      {!receiveMessages ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0 12px' }}>
          <VenueGuestMessagesOffPill />
        </div>
      ) : null}

      {loading ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-3)', textAlign: 'center', paddingTop: 20 }}>Loading messages...</p> : null}
      {!loading && receiveMessages && visibleConversations.length === 0 ? (
        <VenueEmptyState
          icon={MessageSquare}
          title="Your first guest message will appear here"
          description="When a guest opens a chat with your venue, you'll see it light up right in this list."
          testId="venue-messages-empty-state"
          compact
        />
      ) : null}

      {visibleConversations.map((conversation) => {
        const isSupport = conversation.type === 'support';
        return (
          <MessengerListRow
            key={conversation.id}
            name={isSupport ? 'Allure' : (formatChatDisplayName(conversation.other_user) || formatChatDisplayName(conversation.name) || conversation.name || 'Conversation')}
            preview={conversation.last_message || conversation.message || 'Start the conversation'}
            imageUrl={isSupport ? '/notification-icon-192.png' : (conversation.other_user?.image_url || conversation.other_user?.photo_url || conversation.image_url)}
            unreadCount={conversation.unread_count || conversation.unread || 0}
            timestamp={conversation.last_message_at}
            rowBadge={isSupport ? (
              <SupportRowBadge
                status={conversation.support_status}
                dataTestId={`venue-msg-${conversation.id}-support-badge`}
              />
            ) : null}
            dataTestId={`venue-msg-${conversation.id}`}
            onClick={() => navigate(`/venue/chat/${conversation.id}`)}
          />
        );
      })}
    </MessengerListShell>
  );
}